// Login state machine: 5 states (sign-in, forgot, reset, success, locked)
// driven by useReducer. The `locked` is a side-state set when the backend reports
// a lockout (423/429 locked) and carries an unlockAt timestamp.
//
// Appwrite uses link-based recovery (email contains a link with userId + secret),
// not OTP codes. The "code-sent" state was removed.

export type LoginState =
  | "sign-in"
  | "forgot"
  | "reset"
  | "success"
  | "locked";

export interface LoginContext {
  email: string;
  unlockAt?: string; // ISO timestamp when lockout expires
  error?: string | null;
  // Recovery params (present when user clicks the email link)
  recoveryUserId?: string;
  recoverySecret?: string;
}

export type LoginAction =
  | { type: "go-sign-in" }
  | { type: "go-forgot" }
  | { type: "go-reset"; userId: string; secret: string }
  | { type: "success" }
  | { type: "lock"; unlockAt: string }
  | { type: "error"; message: string }
  | { type: "clear-error" };

export interface LoginReducerState {
  state: LoginState;
  ctx: LoginContext;
}

export const initialLoginState: LoginReducerState = {
  state: "sign-in",
  ctx: { email: "" },
};

export function loginReducer(
  prev: LoginReducerState,
  action: LoginAction,
): LoginReducerState {
  switch (action.type) {
    case "go-sign-in":
      return { state: "sign-in", ctx: { email: prev.ctx.email } };
    case "go-forgot":
      return { state: "forgot", ctx: { email: prev.ctx.email, error: null } };
    case "go-reset":
      return {
        state: "reset",
        ctx: {
          ...prev.ctx,
          recoveryUserId: action.userId,
          recoverySecret: action.secret,
          error: null,
        },
      };
    case "success":
      return { state: "success", ctx: prev.ctx };
    case "lock":
      return { state: "locked", ctx: { ...prev.ctx, unlockAt: action.unlockAt, error: null } };
    case "error":
      return { state: prev.state, ctx: { ...prev.ctx, error: action.message } };
    case "clear-error":
      return { state: prev.state, ctx: { ...prev.ctx, error: null } };
    default:
      return prev;
  }
}
